const MAX_BYTES = 5 * 1024 * 1024;

const MIME_EXT: Record<ProfileImageMime, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

export type ProfileImageMime = 'image/jpeg' | 'image/png' | 'image/webp';

export interface ValidatedProfileImage {
  file: File;
  mime: ProfileImageMime;
  fileName: string;
}

async function readFileHeader(file: Blob, length: number): Promise<Uint8Array> {
  const slice = file.slice(0, length);
  if (typeof slice.arrayBuffer === 'function') {
    const buffer = await slice.arrayBuffer();
    return new Uint8Array(buffer.slice(0, length));
  }
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsArrayBuffer(slice);
  });
}

function detectImageMime(bytes: Uint8Array): ProfileImageMime | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return 'image/png';
  }
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'image/webp';
  }
  return null;
}

export function safeProfileImageFileName(mime: ProfileImageMime): string {
  const ext = MIME_EXT[mime];
  return `${Date.now()}-${crypto.randomUUID().slice(0, 8)}.${ext}`;
}

/** Returns an i18n toast key when invalid, or null for a quick size/type pre-check. */
export function validateProfileImageFileQuick(file: File): string | null {
  if (file.size > MAX_BYTES) {
    return 'profile.toast.backgroundInvalid';
  }
  return null;
}

/** Validates file size and magic bytes; returns toast key or validated metadata. */
export async function validateProfileImageFile(
  file: File,
): Promise<string | ValidatedProfileImage> {
  const quick = validateProfileImageFileQuick(file);
  if (quick) return quick;

  const header = await readFileHeader(file, 12);
  const mime = detectImageMime(header);
  if (!mime) {
    return 'profile.toast.backgroundInvalid';
  }

  return {
    file,
    mime,
    fileName: safeProfileImageFileName(mime),
  };
}
